import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, Volume2 } from 'lucide-react';

const formatTime = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

export const MessengerAudioPlayer = ({ src, dataTestId, compact = true }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return undefined;
    }

    const handleLoadedMetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [src]);

  const progressPercent = useMemo(() => {
    if (!duration) {
      return 0;
    }
    return Math.min(100, (currentTime / duration) * 100);
  }, [currentTime, duration]);

  const waveformBars = useMemo(() => [0.34, 0.52, 0.42, 0.76, 0.58, 0.86, 0.47, 0.63, 0.38, 0.82, 0.56, 0.68, 0.44, 0.88, 0.54, 0.72, 0.41, 0.79, 0.49, 0.66, 0.36, 0.74, 0.46, 0.61], []);

  const seekToRatio = (ratio) => {
    const audio = audioRef.current;
    if (!audio || !duration) {
      return;
    }
    const boundedRatio = Math.min(1, Math.max(0, ratio));
    audio.currentTime = duration * boundedRatio;
    setCurrentTime(audio.currentTime);
  };

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      setIsPlaying(false);
    }
  };

  const updateScrubPosition = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width) {
      return;
    }
    const ratio = (event.clientX - bounds.left) / bounds.width;
    seekToRatio(ratio);
  };

  return (
    <div
      data-testid={dataTestId}
      style={{
        width: compact ? 248 : '100%',
        maxWidth: '100%',
        borderRadius: compact ? 20 : 24,
        padding: compact ? '10px 12px' : '14px 16px',
        background: 'linear-gradient(180deg, rgba(250,248,255,0.96), rgba(245,241,255,0.92))',
        border: '1px solid rgba(112, 63, 246, 0.12)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 14 }}>
        <button
          type="button"
          onClick={togglePlayback}
          data-testid={`${dataTestId}-toggle-button`}
          style={{
            width: compact ? 38 : 46,
            height: compact ? 38 : 46,
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #7B4EFF, #693AF2)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 18px rgba(112, 63, 246, 0.22)',
            flexShrink: 0,
          }}
        >
          {isPlaying ? <Pause size={compact ? 16 : 18} fill="#FFFFFF" /> : <Play size={compact ? 16 : 18} fill="#FFFFFF" />}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <Volume2 size={compact ? 14 : 16} color="#6B5AA9" />
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: compact ? 14 : 16, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Voice message
              </span>
            </div>
            <span data-testid={`${dataTestId}-time`} style={{ fontFamily: "'Inria Serif', serif", fontSize: compact ? 13 : 14, color: 'var(--text-2)', flexShrink: 0 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div
            role="slider"
            aria-label="Voice message waveform"
            data-testid={`${dataTestId}-waveform`}
            onPointerDown={(event) => {
              setIsScrubbing(true);
              event.currentTarget.setPointerCapture?.(event.pointerId);
              updateScrubPosition(event);
            }}
            onPointerMove={(event) => {
              if (isScrubbing) {
                updateScrubPosition(event);
              }
            }}
            onPointerUp={(event) => {
              if (isScrubbing) {
                updateScrubPosition(event);
              }
              setIsScrubbing(false);
              event.currentTarget.releasePointerCapture?.(event.pointerId);
            }}
            onPointerCancel={() => setIsScrubbing(false)}
            onClick={updateScrubPosition}
            style={{ width: '100%', cursor: 'pointer', padding: compact ? '3px 0 2px' : '5px 0 4px', touchAction: 'none' }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${waveformBars.length}, minmax(0, 1fr))`, alignItems: 'end', gap: compact ? 3 : 4, minHeight: compact ? 28 : 36 }}>
              {waveformBars.map((heightRatio, index) => {
                const barProgress = (index + 1) / waveformBars.length;
                const isActive = progressPercent / 100 >= barProgress;
                return (
                  <div
                    key={`${dataTestId}-bar-${index}`}
                    data-testid={`${dataTestId}-waveform-bar-${index}`}
                    style={{
                      height: `${(compact ? 24 : 32) * heightRatio}px`,
                      borderRadius: 999,
                      background: isActive ? 'linear-gradient(180deg, #8F70FF, #6F42F4)' : 'rgba(112, 63, 246, 0.18)',
                      boxShadow: isActive ? '0 4px 10px rgba(112, 63, 246, 0.16)' : 'none',
                      opacity: isScrubbing ? 0.96 : 1,
                      transform: isScrubbing && isActive ? 'scaleY(1.06)' : 'scaleY(1)',
                      transition: 'background 160ms ease, box-shadow 160ms ease, transform 160ms ease, opacity 160ms ease',
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};